import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { billingService } from '../services/billing';
import { PlanComparison, PaymentHistory, UsageLimits } from '../components/User';
import toast from 'react-hot-toast';

function Billing() {
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [isCapturingPayPal, setIsCapturingPayPal] = useState(false);

  useEffect(() => {
    const syncBillingState = async () => {
      const provider = searchParams.get('provider');
      const paypalOrderId = searchParams.get('paypal_order_id');

      if (provider === 'paypal' && paypalOrderId) {
        setIsCapturingPayPal(true);
        try {
          const result = await billingService.capturePayPalOrder(paypalOrderId);
          toast.success('PayPal payment confirmed successfully');
          if (!result.order_email_sent && result.order_email_error) {
            toast.error(`Order email failed: ${result.order_email_error}`);
          }
        } catch (error) {
          const message = error.response?.data?.detail || 'Failed to confirm PayPal payment';
          toast.error(message);
        } finally {
          setIsCapturingPayPal(false);
        }
      } else if (searchParams.get('success') === 'true') {
        toast.success('Subscription updated successfully!');
      } else if (searchParams.get('canceled') === 'true') {
        toast.error('Checkout was canceled');
      }

      loadBillingData();
    };

    syncBillingState();
  }, [searchParams]);

  const loadBillingData = async () => {
    setIsLoading(true);
    try {
      const subData = await billingService.getSubscription().catch(() => null);
      setSubscription(subData);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = async (plan, billingCycle) => {
    if (plan.price_monthly === 0) {
      toast.error('You are already on the free plan');
      return;
    }

    try {
      const { url } = await billingService.createCheckoutSession(
        plan.id,
        billingCycle,
        paymentProvider
      );
      window.location.href = url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(message);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { url } = await billingService.getBillingPortalUrl();
      window.location.href = url;
    } catch (error) {
      toast.error('Failed to open billing portal');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8">Billing & Subscription</h1>
      {isCapturingPayPal && (
        <p className="text-sm text-primary-400 mb-4">Finalizing PayPal payment...</p>
      )}

      <div className="grid lg:grid-cols-3 gap-8 mb-8">
        {/* Current Subscription */}
        <div className="lg:col-span-2">
          {subscription && (
            <div className="card">
              <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
              
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-2xl font-bold capitalize">{subscription.tier}</p>
                  <p className="text-gray-400">
                    Status: <span className={subscription.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>
                      {subscription.status}
                    </span>
                  </p>
                  {subscription.current_period_end && (
                    <p className="text-gray-400 text-sm">
                      Renews: {new Date(subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
                
                {subscription.tier !== 'free' && (
                  <button onClick={handleManageBilling} className="btn btn-secondary">
                    Manage Billing
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-6 border-t border-gray-700">
                <div>
                  <p className="text-gray-400 text-sm">Daily Limit</p>
                  <p className="text-lg font-semibold">{subscription.daily_limit}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Monthly Limit</p>
                  <p className="text-lg font-semibold">{subscription.monthly_limit}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Max Characters</p>
                  <p className="text-lg font-semibold">{subscription.max_characters}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Workspace Seats</p>
                  <p className="text-lg font-semibold">{subscription.workspace_seats || 1}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Voice Cloning</p>
                  <p className="text-lg font-semibold">
                    {subscription.voice_cloning_enabled ? `${subscription.max_cloned_voices} voices` : 'No'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Usage Sidebar */}
        <div>
          <UsageLimits />
        </div>
      </div>

      {/* Payment Provider Selection */}
      <div className="card mb-8">
        <h3 className="text-lg font-semibold mb-4">Payment Method</h3>
        <div className="flex gap-4">
          <button
            onClick={() => setPaymentProvider('stripe')}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
              paymentProvider === 'stripe'
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-gray-700 hover:border-gray-600'
            }`}
          >
            <svg className="w-8 h-5" viewBox="0 0 60 25" fill="currentColor">
              <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a12.64 12.64 0 0 1-4.56.78c-4.14 0-6.97-2.57-6.97-7.28 0-4.22 2.5-7.22 6.34-7.22 3.88 0 6.05 2.86 6.05 7.27 0 .43-.03.97-.05 1.53zm-4.71-5.25c-.74 0-2.03.53-2.03 2.72h4.07c0-2.24-1.28-2.72-2.04-2.72zm-13.54 11.64c-1.5 0-2.29-.6-2.83-1.02l-.01 4.42-4.24.9V5.88h3.81l.15 1.05c.54-.8 1.57-1.2 2.93-1.2 2.68 0 5.17 2.43 5.17 6.95 0 5.22-2.43 7.9-5.08 7.9l.1.09zm-.82-11.27c-.93 0-1.49.39-1.89.87l.02 5.37c.38.47.92.87 1.87.87 1.44 0 2.44-1.59 2.44-3.58 0-1.93-.98-3.53-2.44-3.53zM24.64.89l4.26-.9v4.26h3.31v3.34h-3.31v5.31c0 1.26.54 1.63 1.4 1.63.57 0 1.25-.18 1.6-.32l.88 3.18c-.58.24-1.71.55-3.1.55-2.84 0-5.03-1.47-5.03-4.93V7.59h-1.95V4.25h1.94V.89zm-7.94 3.21c1.37 0 2.57.12 3.56.42v3.8c-.88-.34-1.8-.54-2.88-.54-1.53 0-2.29.63-2.29 1.35 0 .84.98 1.19 2.22 1.67 1.86.73 3.86 1.77 3.86 4.42 0 3.1-2.53 4.72-5.68 4.72-1.6 0-3.08-.19-4.4-.65v-3.88c.95.47 2.43.95 3.63.95 1.53 0 2.2-.52 2.2-1.3 0-.88-.97-1.24-2.21-1.75-1.86-.75-3.76-1.7-3.76-4.26 0-2.93 2.3-4.95 5.75-4.95zM4.24 15.83c0-3.66-6.35-2.23-6.35-8.5C-2.1 3.4 1.12.72 5.6.72c1.72 0 3.38.36 4.75.95v4.1c-1.34-.76-2.86-1.13-4.2-1.13-1.56 0-2.42.52-2.42 1.35 0 2.84 6.4 1.7 6.4 8.31 0 3.87-2.77 6.43-6.68 6.43-1.85 0-3.9-.37-5.4-1.12v-4.24c1.66 1.01 3.6 1.62 5.16 1.62 1.3 0 2.14-.42 2.14-1.16h-.1z"/>
            </svg>
            <span>Credit Card</span>
          </button>
          
          <button
            onClick={() => setPaymentProvider('paypal')}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
              paymentProvider === 'paypal'
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-gray-700 hover:border-gray-600'
            }`}
          >
            <svg className="w-8 h-5" viewBox="0 0 101 32" fill="currentColor">
              <path d="M12.237 5.95H4.21a.96.96 0 0 0-.95.81L.253 24.5a.575.575 0 0 0 .569.664h3.836a.96.96 0 0 0 .949-.81l.807-5.115a.96.96 0 0 1 .949-.81h2.19c4.555 0 7.183-2.204 7.87-6.573.308-1.91.012-3.41-.88-4.46-.98-1.154-2.718-1.446-5.306-1.446zm.8 6.475c-.378 2.48-2.272 2.48-4.105 2.48h-1.04l.732-4.632a.575.575 0 0 1 .569-.488h.477c1.248 0 2.427 0 3.035.71.364.426.475 1.056.331 1.93z"/>
              <path d="M35.918 12.338h-3.854a.575.575 0 0 0-.568.488l-.17 1.076-.27-.391c-.837-1.214-2.7-1.62-4.561-1.62-4.266 0-7.91 3.232-8.621 7.768-.369 2.261.155 4.422 1.44 5.93 1.18 1.386 2.867 1.963 4.875 1.963 3.447 0 5.36-2.216 5.36-2.216l-.172 1.074a.576.576 0 0 0 .569.664h3.47a.96.96 0 0 0 .949-.81l2.084-13.21a.575.575 0 0 0-.531-.716zm-5.394 7.521c-.371 2.2-2.117 3.677-4.347 3.677-1.12 0-2.014-.36-2.588-1.04-.57-.675-.785-1.637-.607-2.708.348-2.18 2.123-3.7 4.32-3.7 1.096 0 1.983.363 2.566 1.05.587.691.82 1.66.656 2.721z"/>
              <path d="M60.595 12.338h-3.871a.958.958 0 0 0-.792.419l-4.57 6.73-1.937-6.468a.96.96 0 0 0-.92-.681h-3.802a.576.576 0 0 0-.546.757l3.65 10.712-3.433 4.843a.576.576 0 0 0 .47.908h3.868a.958.958 0 0 0 .787-.41l11.02-15.914a.576.576 0 0 0-.474-.896z"/>
            </svg>
            <span>PayPal</span>
          </button>
        </div>
      </div>

      {/* Plan Comparison */}
      <h2 className="text-xl font-semibold mb-4">Available Plans</h2>
      <PlanComparison 
        currentPlan={subscription?.tier || 'free'} 
        onSelectPlan={handleSelectPlan} 
      />

      {/* Payment History Toggle */}
      <div className="mt-12">
        <button
          onClick={() => setShowPaymentHistory(!showPaymentHistory)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <span>{showPaymentHistory ? '▼' : '▶'}</span>
          Payment History
        </button>
        
        {showPaymentHistory && (
          <div className="mt-4">
            <PaymentHistory />
          </div>
        )}
      </div>
    </div>
  );
}

export default Billing;
